import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { serviceCityHeadScripts } from "@/lib/cityJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { getCityHub, CITY_HUBS, type CityHub } from "@/data/cityHubs";
import hero from "@/assets/marine-canvas-boat.jpg";

import heroPicture from "@/assets/marine-canvas-boat.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
export const Route = createFileRoute("/boat-covers/in/$city")({
  beforeLoad: ({ params }) => {
    const hub = getCityHub(params.city);
    if (!hub) throw notFound();
  },
  loader: ({ params }) => ({ hub: getCityHub(params.city)! }),
  head: (ctx) => {
    const hub = getCityHub(ctx.params.city);
    if (!hub) return {};
    const title = `Boat Covers in ${hub.name}, TX | Texas Made Shade`;
    const description = `Custom marine canvas in ${hub.name}, TX — boat covers, bimini tops, T-top canvas and mooring covers. Sunbrella® Marine, YKK marine zippers. Patterned to your boat.`;
    return {
      links: [canonicalLink(`/boat-covers/in/${hub.slug}`)],
      meta: routeMeta(ctx, {
        title,
        description,
        image: hero,
        imageAlt: `Custom marine canvas boat cover for ${hub.name}, TX`,
        socialTitle: `Custom Boat Covers in ${hub.name}, TX`,
        socialDescription: description,
      }),
      scripts: [
        ...serviceCityHeadScripts({
          path: `/boat-covers/in/${hub.slug}`,
          serviceName: "Boat Covers & Marine Canvas",
          city: hub.name,
          region: hub.region,
          parentPath: "/boat-covers",
          parentName: "Boat Covers",
        }),
        ...serviceHeadScripts({
          path: `/boat-covers/in/${hub.slug}`,
          serviceName: `Custom Boat Covers in ${hub.name}, TX`,
          breadcrumbName: `Boat Covers in ${hub.name}`,
          description,
        }),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `Custom Boat Covers in ${hub.name}, TX`,
            serviceType: "Marine Canvas Fabrication",
            url: canonicalUrl(`/boat-covers/in/${hub.slug}`),
            provider: { "@id": "https://texasmadeshade.com/#business" },
            areaServed: {
              "@type": "City",
              name: hub.name,
              containedInPlace: { "@type": "AdministrativeArea", name: hub.region },
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main id="main-content" tabIndex={-1} className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-4xl font-bold text-navy-deep">City not found</h1>
      <p className="mt-4 text-navy-deep/75">
        We don't have a dedicated boat cover page for that city yet.{" "}
        <Link to="/boat-covers" className="text-red underline">See our main boat covers page</Link>.
      </p>
    </main>
  ),
  component: BoatCoversCityPage,
});

function BoatCoversCityPage() {
  const { hub } = Route.useLoaderData() as { hub: CityHub };
  const nearby = hub.nearby
    .map((s) => getCityHub(s))
    .filter((c): c is CityHub => Boolean(c));

  return (
    <ServicePage
      currentPath={`/boat-covers/in/${hub.slug}`}
      serviceName={`Custom Boat Covers in ${hub.name}`}
      breadcrumbs={[
        { label: "Boat Covers", to: "/boat-covers" },
        { label: `Boat Covers in ${hub.name}, TX` },
      ]}
      eyebrow={`★ MARINE CANVAS · ${hub.name.toUpperCase()}, TX ★`}
      title={`Custom Boat Covers in ${hub.name}, TX`}
      intro={`Custom marine canvas patterned to your boat — covers, bimini tops, T-top canvas, mooring covers and bay-boat shade. Built in our Seguin shop with Sunbrella® Marine canvas and YKK marine zippers.`}
      heroImage={heroPicture}
      heroCta={{
        eyebrow: `Marine Canvas Shop · Serving ${hub.name}, TX`,
        headline: `${hub.name} Boat Cover Quote — Patterned to Your Hull`,
        subhead: `Send make/model and a few photos of your ${hub.name}-area boat. We'll come back with fabric options and a written estimate.`,
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Boat Cover")}&notes=${encodeURIComponent(`Boat cover quote — boat located in/near ${hub.name}, TX. Please describe make, model and cover type.`)}`,
        primaryLabel: `Get My ${hub.name} Boat Cover Quote`,
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        `Custom marine canvas for ${hub.name}, TX`,
        "Sunbrella® Marine + YKK marine zippers",
        "Boat covers, bimini tops, T-top canvas",
        "Bay boat, center console & offshore covers",
        "Patterned to your hull (no off-the-shelf fits)",
        `Serving ${hub.name} and surrounding ${hub.region}`,
      ]}
      body={[
        {
          heading: `Why ${hub.name} Boaters Choose Custom Canvas`,
          text: `Off-the-shelf covers leave gaps where UV and rain do their worst damage. Every cover we build for ${hub.name} customers is patterned to your specific boat — snug, quiet, and sized to last 8+ Texas seasons.`,
        },
        {
          heading: `Bimini Tops & T-Top Canvas in ${hub.name}`,
          text: `New bimini tops, replacement T-top canvas and shade extensions for guide boats, center consoles and bay boats running ${hub.region.toLowerCase()} water. We come measure or you trailer to our Seguin shop.`,
          cta: { label: "See Marine Covers & Tops", href: "/covers-tops" },
        },
        {
          heading: `Mooring & Storage Covers for ${hub.name} Boats`,
          text: `Long-term storage covers with reinforced corners, support poles and tie-down systems engineered for ${hub.region.includes("Coast") ? "Gulf Coast wind and salt" : "Texas sun and dust"}. Built to actually stay on.`,
        },
      ]}
      applications={[
        "Bay Boat Covers",
        "Center Console Covers",
        "Offshore Boat Covers",
        "Air Boat Covers",
        "Bimini Tops",
        "T-Top Canvas",
        "Mooring Covers",
        "Boat Cushion Re-Cover",
      ]}
      faqs={[
        {
          q: `Do you make custom boat covers in ${hub.name}, TX?`,
          a: `Yes — we pattern, sew and fit custom marine canvas for boats in ${hub.name} and the surrounding ${hub.region}. You can trailer to our Seguin shop or schedule on-site patterning.`,
        },
        {
          q: "What fabric do you use for boat covers?",
          a: "Sunbrella® Marine solution-dyed acrylic for tops and covers, paired with YKK marine zippers and bonded marine thread. Both carry the longest available UV warranties.",
        },
        {
          q: `How long does a custom ${hub.name} boat cover take?`,
          a: "Most boat cover and bimini builds run 2–4 weeks depending on patterning schedule and fabric lead time. Rush turnaround available for ramp-launching deadlines.",
        },
        {
          q: "Can you re-skin a worn-out bimini top?",
          a: "Yes — bring us the bimini frame (or send photos) and we'll cut and sew a brand-new Sunbrella Marine skin to the existing bows. A fraction of the cost of replacing the whole top.",
        },
      ]}
      cityCallouts={[
        {
          to: `/${hub.slug}` as string,
          city: hub.name,
          anchorText: `All Texas Made Shade services in ${hub.name}, TX`,
          blurb: `See every awning, cover, sail-shade and welding service we offer to ${hub.name}.`,
        },
        ...nearby.slice(0, 3).map((n) => ({
          to: `/boat-covers/in/${n.slug}` as string,
          city: n.name,
          anchorText: `Boat covers in ${n.name}, TX`,
          blurb: `We also build custom marine canvas for boats in nearby ${n.name} (${n.region}).`,
        })),
      ]}
      postCta={(
        <section className="border-t-4 border-navy-deep bg-white py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="text-center">
              <div className="font-display text-sm tracking-[0.3em] text-red">★ MORE CITIES WE BUILD BOAT COVERS FOR</div>
              <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                Marine Canvas Across Texas
              </h2>
            </div>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {CITY_HUBS.filter((c) => c.slug !== hub.slug).map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/boat-covers/in/$city"
                    params={{ city: c.slug }}
                    className="block border-l-4 border-red bg-cream px-4 py-3 font-display text-sm font-bold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
                  >
                    Boat Covers in {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    />
  );
}
